import * as React from 'react';
import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Routes, useJourneyNavigation } from '../../../routes';
import { OrgPageWidget } from '../../../widgets/page/OrgPageWidget';
import { PageHeadingWidget } from '../../../widgets/page/PageHeadingWidget';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { TicketReplyWidget } from './TicketReplyWidget';
import { PageMode } from '../../../widgets/page/PageWidget';
import { styled } from '../../../theme';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { SmartTicketReplyWidget } from './SmartTicketReplyWidget';
import { ButtonType, ButtonWidget } from '../../../widgets/buttons/ButtonWidget';
import { TicketStatus } from '@journeyapps-platform/types-intercom';
import { useSystem } from '../../../hooks/useSystem';

export const SupportTicketPage: React.FC = observer(() => {
  const orgStore = useOrgStore();
  const system = useSystem();
  const { relativeRoute, navigate, params } = useJourneyNavigation(Routes.SUPPORT_TICKET);
  const org = orgStore.current_org!;

  useEffect(() => {
    org?.getTicket(params.ticket_id);
  }, [org, params.ticket_id]);

  let ticket = org.ticket(params.ticket_id);
  useEffect(() => {
    ticket?.loadAllReplies();
  }, [ticket]);

  const ticket_open = ticket?.ticket.status === TicketStatus.OPEN;

  return (
    <OrgPageWidget activeItemKey={Routes.SUPPORT} mode={PageMode.FULLSCREEN}>
      <S.Content>
        <PanelGroup direction="vertical">
          <S.Top>
            <S.Heading
              title={`Support Ticket > ${ticket?.ticket.subject}`}
              extra={
                ticket_open ? (
                  <S.Buttons>
                    <ButtonWidget
                      type={ButtonType.SECONDARY}
                      icon="rectangle-xmark"
                      label="Close Ticket"
                      action={async (event, setLoading) => {
                        setLoading(true);
                        try {
                          await ticket.update({
                            status: TicketStatus.CLOSED
                          });

                          navigate(Routes.SUPPORT, { org_id: org.org_id });
                        } catch (ex) {
                          system.dialogStore.errorDialog({
                            title: `Oops, something went wrong.`,
                            errorMessage: 'We were unable to close the ticket, see the console for more details.'
                          });
                        } finally {
                          setLoading(false);
                        }
                      }}
                    />
                  </S.Buttons>
                ) : null
              }
            />
            <S.ScrollContent>
              {(ticket?.replies?.items || []).map((t) => {
                return <S.TicketReply ticket={t} key={t.id} />;
              })}
            </S.ScrollContent>
          </S.Top>
          {ticket_open ? (
            <>
              <PanelResizeHandle />
              <Panel maxSize={50} defaultSize={20} minSize={20}>
                <S.Input
                  org={org}
                  ticket_id={params.ticket_id}
                  replySent={() => {
                    ticket?.loadAllReplies();
                  }}
                />
              </Panel>
            </>
          ) : null}
        </PanelGroup>
      </S.Content>
    </OrgPageWidget>
  );
});

namespace S {
  export const TicketReply = styled(TicketReplyWidget)`
    margin-bottom: 10px;
  `;

  export const Heading = styled(PageHeadingWidget)`
    padding: 20px;
  `;

  export const Top = styled(Panel)`
    display: flex;
    flex-direction: column;
    height: 100%;
  `;

  export const Input = styled(SmartTicketReplyWidget)`
    flex-shrink: 0;
    border-top: 1px solid ${(p) => p.theme.sidebar.borderColor};
    padding: 20px;
    background: ${(p) => p.theme.background};
    color: ${(p) => p.theme.text.primary};
    height: 100%;
    display: flex;
  `;

  export const ScrollContent = styled.div`
    flex-grow: 1;
    height: 100%;
    min-height: 0;
    overflow: auto;
    padding: 20px;
  `;

  export const Content = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    position: absolute;
    width: 100%;
  `;

  export const Buttons = styled.div`
    display: flex;
    column-gap: 5px;
  `;
}
